import React, { useRef, useState } from 'react'
import { useWebHaptics } from 'web-haptics/react'

import { useCanEditDeck } from 'app-redux/deck/selectors'
import { useSwipeBackForthEffect } from 'hooks/useSwipeEffects'

import DeckCardWrapper, { DeckCardWrapperChildProps } from 'components/card/deckCards/deckCardWrapper'
import Icon from 'components/elements/Icon'
import ContextMenu from 'components/elements/ContextMenu'
import Prices from 'components/card/controlsAndInfo/prices'
import FoilSquare from 'components/card/controlsAndInfo/foilSquare'
import CollectionDot from 'components/card/controlsAndInfo/collectionDot'
import ColorTagCssDropdown from 'components/card/controlsAndInfo/colorTagCssDropdown'
import PinnedIndicator from 'components/card/controlsAndInfo/pinnedIndicator'
import SimpleSpinner from 'components/elements/SimpleSpinner'
import DrawerMenu from 'components/elements/DrawerMenu'

import { generateContextOptions } from 'components/card/deckCards/deckCardContextMenu'
import { getImageUrl } from 'utils/ImageUrl'
import { CardText, createTypeLine, manaArrayToElement } from 'components/misc/customIcons/ManaSymbol/helpers'
import { colorsToColorIdentityName } from 'utils/colorUtils'

import styles from './listCard.module.scss'
import mtgColorBorders from './mtgColorBorders.module.scss'

type Props = { cardId: string; stackName?: string }

const ListCard = ({ cardId, stackName }: Props) => (
  <DeckCardWrapper cardId={cardId} stackName={stackName} component={Card} />
)

export default ListCard

const GestureZero = 0
const GestureOnePosition = 0.15
const GestureTwoPosition = 0.3
const GestureThreePosition = 0.45
const GestureStopPosition = 0.55

const Card = ({ card, localCardData, deckData, userData, actions, extraData }: DeckCardWrapperChildProps) => {
  const canEditDeck = useCanEditDeck()

  const { trigger } = useWebHaptics()

  const [categoryDrawer, setCategoryDrawerOpen] = useState(false)

  const handleBuzz = () => trigger([{ duration: 12 }], { intensity: 1 })

  const previousThresholdCrossed = useRef(0) // Stored as a percent

  const handleMoveToGestureZoneOne = () => {
    previousThresholdCrossed.current = GestureOnePosition

    swipeSecondaryRef.current?.classList.add(styles.gesturePositionOne)
    swipeSecondaryRef.current?.classList.remove(styles.gesturePositionTwo)
    swipeSecondaryRef.current?.classList.remove(styles.gesturePositionThree)

    handleBuzz()
  }

  const handleMoveToGestureZoneTwo = () => {
    previousThresholdCrossed.current = GestureTwoPosition

    swipeSecondaryRef.current?.classList.remove(styles.gesturePositionOne)
    swipeSecondaryRef.current?.classList.add(styles.gesturePositionTwo)
    swipeSecondaryRef.current?.classList.remove(styles.gesturePositionThree)

    handleBuzz()
  }

  const handleMoveToGestureZoneThree = () => {
    previousThresholdCrossed.current = GestureThreePosition

    swipeSecondaryRef.current?.classList.remove(styles.gesturePositionOne)
    swipeSecondaryRef.current?.classList.remove(styles.gesturePositionTwo)
    swipeSecondaryRef.current?.classList.add(styles.gesturePositionThree)

    handleBuzz()
  }

  const handleMoveToGestureZoneStopOrZero = (zero?: boolean) => {
    previousThresholdCrossed.current = zero ? GestureZero : GestureStopPosition

    swipeSecondaryRef.current?.classList.remove(styles.gesturePositionOne)
    swipeSecondaryRef.current?.classList.remove(styles.gesturePositionTwo)
    swipeSecondaryRef.current?.classList.remove(styles.gesturePositionThree)
  }

  const [swipePrimaryRef, swipeSecondaryRef, swipeCallbacks] = useSwipeBackForthEffect({
    onPrimaryTouchMove: (offset: number, _direction, _position, timeSinceSwipeStart) => {
      if (!swipePrimaryRef.current || !swipeSecondaryRef.current) return

      const percentageSwipped = (-1 * offset) / swipePrimaryRef.current.getBoundingClientRect().width

      if (canEditDeck && offset < 0 && percentageSwipped < GestureStopPosition && timeSinceSwipeStart > 200) {
        // Zero => One
        if (percentageSwipped >= GestureOnePosition && previousThresholdCrossed.current === GestureZero)
          handleMoveToGestureZoneOne()
        // One => Two
        else if (percentageSwipped >= GestureTwoPosition && previousThresholdCrossed.current === GestureOnePosition)
          handleMoveToGestureZoneTwo()
        // Two => Three
        else if (percentageSwipped >= GestureThreePosition && previousThresholdCrossed.current === GestureTwoPosition)
          handleMoveToGestureZoneThree()
        // Stop => Three
        else if (percentageSwipped <= GestureStopPosition && previousThresholdCrossed.current === GestureStopPosition)
          handleMoveToGestureZoneThree()
        // Three => Two
        else if (percentageSwipped <= GestureThreePosition && previousThresholdCrossed.current === GestureThreePosition)
          handleMoveToGestureZoneTwo()
        // Two => One
        else if (percentageSwipped <= GestureTwoPosition && previousThresholdCrossed.current === GestureTwoPosition)
          handleMoveToGestureZoneOne()
        // One | Two | Three => Zero
        else if (percentageSwipped <= GestureOnePosition && previousThresholdCrossed.current !== GestureZero)
          handleMoveToGestureZoneStopOrZero(true)
      }

      // One | Two | Three => Stop
      if (percentageSwipped >= GestureStopPosition && previousThresholdCrossed.current === GestureThreePosition)
        handleMoveToGestureZoneStopOrZero(false)
    },
    onPrimaryTouchEnd: () => {
      setTimeout(() => {
        if (!swipeSecondaryRef.current) return

        if (previousThresholdCrossed.current === GestureOnePosition) setCategoryDrawerOpen(true)
        if (previousThresholdCrossed.current === GestureTwoPosition) actions.openCardModal(true)
        if (previousThresholdCrossed.current === GestureThreePosition) actions.removeCard()

        swipeSecondaryRef.current.classList.remove(styles.gesturePositionOne)
        swipeSecondaryRef.current.classList.remove(styles.gesturePositionTwo)
        swipeSecondaryRef.current.classList.remove(styles.gesturePositionThree)

        previousThresholdCrossed.current = 0
      }, 50)
    },
  })

  const colorIdentityName = colorsToColorIdentityName(card.colorIdentity)
  const options = generateContextOptions({ card, actions, localCardData, deckData, extraData, userData }, false, true)
  const moveToCategoryOptions = options.find(opt => opt.id === 'move-to-category') || []

  // Add item to options list specific to this view at index 2
  options.splice(3, 0, { label: 'View card text', icon: 'eye', onClick: swipeCallbacks.handleSwipeInSecondary })

  const colorBorderClassName = mtgColorBorders[colorIdentityName]
    ? mtgColorBorders[colorIdentityName]
    : mtgColorBorders.MultiColor

  const Background = (
    <div
      className={`${styles.background} ${localCardData.inSecondaryCategory ? styles.secondaryCategory : ''}`}
      style={{ backgroundImage: `url(${getImageUrl(card.setCode, card.uid, card.flippedDefault, 'art_crop')})` }}
    />
  )

  const DrawerTopper = (
    <div className={styles.customMenuOption}>
      <div className={`${styles.cardMenu} ${colorBorderClassName}`}>
        <div className={styles.cardName}>
          {card.name} {manaArrayToElement(card.castingCost)}
        </div>

        <div className={styles.info}>
          <span>Quantity {localCardData.quantity}</span>
          <FoilSquare card={card} includeLabel />
        </div>

        {Background}
      </div>
    </div>
  )

  return (
    <>
      <div className={styles.container}>
        <div
          ref={swipePrimaryRef}
          onClick={actions.cardClick}
          onContextMenu={actions.rightClick}
          className={`${styles.card} ${localCardData.cardError ? styles.cardError : ''} ${localCardData.isMultiSelected ? styles.selected : ''} ${colorBorderClassName}`}>
          <div className={styles.left}>
            <div className={styles.primaryContent}>
              <button
                onClick={actions.cardClick}
                className={`${styles.cardName} ${localCardData.inSecondaryCategory ? styles.secondaryCategory : ''}`}>
                {card.name} {manaArrayToElement(card.castingCost)}
              </button>
              <div className={styles.info}>
                <span>Quantity {localCardData.quantity}</span>
                <ColorTagCssDropdown includeLabel card={card} />
                <FoilSquare card={card} includeLabel />
              </div>

              <div className={styles.info}>
                <span>
                  <CollectionDot card={card} /> Collection
                </span>

                <PinnedIndicator card={card} />
              </div>

              <div className={styles.info}></div>
            </div>

            <Prices card={card} className={styles.prices} />
          </div>

          {!localCardData.anyMultiSelected && (
            <ContextMenu
              className={styles.right}
              customOptionsTop={
                <div className={styles.customMenuOption}>
                  <div className={`${styles.cardMenu} ${colorBorderClassName}`}>
                    <div className={styles.cardName}>
                      {card.name} {manaArrayToElement(card.castingCost)}
                    </div>

                    <div className={styles.info}>
                      <span>Quantity {localCardData.quantity}</span>
                      <FoilSquare card={card} includeLabel />
                    </div>

                    {Background}
                  </div>
                </div>
              }
              options={options}
              openOnLeftClick
              forceOpenAsDrawer>
              <button className={styles.dropdownTrigger}>
                {localCardData.saving !== 'saving' ? (
                  <Icon name="ellipsis horizontal" />
                ) : (
                  <SimpleSpinner size="xSmall" />
                )}
              </button>
            </ContextMenu>
          )}

          {Background}
        </div>

        <button
          ref={ref => {
            // @ts-expect-error - Using hook built for divs on button. Should be fine, I'm not doing anything too weird - would love to figure out a way to make this type happy though
            if (swipeSecondaryRef) swipeSecondaryRef.current = ref
          }}
          onClick={e => {
            actions.cardClick(e)

            // No need for the delay if any are multi-selected
            if (localCardData.anyMultiSelected) return swipeCallbacks.handleSwipeInPrimary()

            setTimeout(swipeCallbacks.handleSwipeInPrimary, 350) // Adding a slight delay here so the overlay can open before swiping back
          }}
          className={`${styles.secondaryCard} ${colorBorderClassName}`}>
          <div className={styles.typeLine}>{createTypeLine(card, false, true)}</div>
          <div className={styles.cardText}>
            <CardText input={card.front?.text || card.text} />
          </div>
        </button>
      </div>

      <DrawerMenu
        open={categoryDrawer}
        onClose={() => setCategoryDrawerOpen(false)}
        minInitalOpenHeight={50}
        maxInitialOpenHeight={50}
        // @ts-ignore
        options={moveToCategoryOptions?.options || []}
      />
    </>
  )
}
